// Browser-side OpenTelemetry bootstrap.
//
// Initialised once on first client load via a side-effect import from
// `instrumentation-client.ts` (which itself is loaded by Next.js's client
// instrumentation hook). Hand-picked instrumentations to keep the gzipped
// bundle small.
//
// Design choices (read before tweaking):
//
// 1. NO `UserInteractionInstrumentation`. Clicks/submits that don't hit an
//    API don't need traces. The `withSpan()` helper in form handlers
//    already creates business-meaningful parent spans for the actions
//    that DO hit the API; the fetch's auto-instrumented span becomes a
//    child via active context.
//
// 2. `FetchInstrumentation.ignoreUrls` keeps the span scope tight to
//    OUR API. Mixpanel, Sentry tunnel, OIDC silent renew, S3
//    whitelabels, and our own `/diag/ingest` (would loop) are explicitly
//    excluded. We don't want third-party hostnames showing up in Kibana,
//    and self-referential telemetry-of-telemetry is meaningless noise.
//
// 3. A custom `StripNetworkTimingEventsProcessor` removes the 9
//    PerformanceEntry events (fetchStart, domainLookupStart, etc.) that
//    `FetchInstrumentation` attaches to every span. Elastic's OTLP
//    ingest splits each span event into its own log record — without
//    this filter, one fetch produces 10 rows (1 span + 9 events) in the
//    `logs-generic.otel-*` index, drowning out everything else.
//
// Trace flow when a user submits a form:
//   handleGetOtp()
//     -> withSpan('registration.email.requestOtp', ...) opens parent span
//     -> RTK Query .unwrap() -> fetch(<BFF origin>/registration/...)
//        (every BFF call goes to its origin — lib/bffTransport.ts)
//     -> FetchInstrumentation auto-creates HTTP span (child) + injects traceparent
//     -> BFF receives traceparent, opens its own server span on same trace
//     -> BFF's outbound axios calls propagate to Java microservices
//     -> All linked by trace_id in Kibana
//
// Exporter: OTLP/HTTP JSON to the BFF's /diag/ingest, cross-origin like every
// BFF call. The exporter's fetch transport posts with `keepalive: true` and
// `mode: "cors"`; its one header (`Content-Type: application/json`) is in the
// BFF's CORS allowedHeaders, and the BFF caches the preflight for 7200s, so a
// cold preflight at page hide is rare. JSON over protobuf to avoid pulling
// protobufjs (~33 KB gzipped) into the browser bundle.
//
// Sentry coexistence: Sentry's `propagateTraceparent` is left at its
// default (false) — OTel SDK owns W3C propagation. A Sentry event
// processor in `instrumentation-client.ts` stamps OTel's active trace ID
// onto Sentry events so Sentry's UI can pivot to Kibana traces.

import { trace } from "@opentelemetry/api";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  type ReadableSpan,
  type Span,
  type SpanProcessor,
  TraceIdRatioBasedSampler,
} from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";

import { bffBaseUrl } from "@/lib/bffBase";
import {
  bffPropagationTargets,
  isBffAllowedPageOrigin,
} from "@/lib/bffTransport";
import { OTEL_FETCH_IGNORE_URLS } from "@/lib/otel/ignoreUrls";
import { resolveClientTelemetry } from "@/lib/telemetryGates";

// ---------------------------------------------------------------------------
// Environment gating — shared with instrumentation-client.ts.
// ---------------------------------------------------------------------------

// Predicate in `lib/telemetryGates.ts` (off in mock mode too). The stored
// mock-mode flag is only read in the browser; the boot block below is
// window-guarded as well.
const telemetry = resolveClientTelemetry();
const env = telemetry.environment;
/** Exported so a test can prove this boot site honours mock mode. */
export const isOtelEnabled = telemetry.otel;

// ---------------------------------------------------------------------------
// Idempotency guard. HMR re-imports this module; Next.js fast refresh may
// double-evaluate. We must not register two TracerProviders.
// ---------------------------------------------------------------------------

declare global {
  var __UP_OTEL_INITIALIZED__: boolean | undefined;
}

// ---------------------------------------------------------------------------
// SpanProcessor wrapper — strips network timing events before export.
// ---------------------------------------------------------------------------

const NETWORK_TIMING_EVENT_NAMES = new Set([
  "fetchStart",
  "domainLookupStart",
  "domainLookupEnd",
  "connectStart",
  "secureConnectionStart",
  "connectEnd",
  "requestStart",
  "responseStart",
  "responseEnd",
]);

/**
 * Wraps another SpanProcessor and strips PerformanceTiming span events
 * (fetchStart, domainLookupStart, ...) before delegating. See the file
 * header for why this is necessary.
 *
 * Mutates `span.events` in place because the OTel SDK's other processors
 * (e.g. Sentry's own SentrySpanProcessor) hold references to the same
 * span object; constructing a new span would diverge their views.
 */
class StripNetworkTimingEventsProcessor implements SpanProcessor {
  constructor(private readonly inner: SpanProcessor) {}

  onStart(
    span: Span,
    parentContext: Parameters<SpanProcessor["onStart"]>[1]
  ): void {
    this.inner.onStart(span, parentContext);
  }

  onEnd(span: ReadableSpan): void {
    if (span.events.length > 0) {
      const filtered = span.events.filter(
        (e) => !NETWORK_TIMING_EVENT_NAMES.has(e.name)
      );
      if (filtered.length !== span.events.length) {
        (span as unknown as { events: typeof filtered }).events = filtered;
      }
    }
    this.inner.onEnd(span);
  }

  shutdown(): Promise<void> {
    return this.inner.shutdown();
  }

  forceFlush(): Promise<void> {
    return this.inner.forceFlush();
  }
}

// A page origin the BFF's CORS list does not admit could not deliver a
// single export, so the SDK is not started there.
if (
  typeof window !== "undefined" &&
  isOtelEnabled &&
  isBffAllowedPageOrigin(window.location.origin) &&
  !globalThis.__UP_OTEL_INITIALIZED__
) {
  globalThis.__UP_OTEL_INITIALIZED__ = true;

  // -------------------------------------------------------------------------
  // PII scrubbing — mirror SENSITIVE_KEYS from apps/bff/src/common/redact.ts.
  // Anything that could leak through a query string gets [REDACTED] in the
  // `http.url` span attribute before the span leaves the browser.
  // -------------------------------------------------------------------------

  const SENSITIVE_QUERY_KEYS = new Set(
    [
      // Credentials
      "password",
      "passcode",
      "passphrase",
      "secret",
      "token",
      "accessToken",
      "refreshToken",
      "apiKey",
      "authorization",
      "cookie",
      // One-time codes
      "otp",
      "otop",
      "oneTimeCode",
      "verificationCode",
      "mfaCode",
      // Card data (PCI)
      "cardNumber",
      "pan",
      "cvv",
      "cvc",
      "cardCvv",
      // Bank identifiers
      "iban",
      "accountNumber",
      "bankAccountNumber",
      "sortCode",
      "routingNumber",
      "bicSwift",
      // Identity
      "dateOfBirth",
      "dob",
      "birthDate",
      "nationality",
      "nationalId",
      "taxId",
      "taxIdentificationNumber",
      "passportNumber",
      "driversLicenseNumber",
      // Address
      "addressLine1",
      "addressLine2",
      "postcode",
      "zipcode",
      // Contact (browser-specific)
      "email",
      "phone",
      "mobile",
      "firstName",
      "lastName",
    ].map((k) => k.toLowerCase())
  );

  const scrubUrl = (raw: string): string => {
    try {
      const u = new URL(raw, window.location.origin);
      for (const key of Array.from(u.searchParams.keys())) {
        if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
          u.searchParams.set(key, "[REDACTED]");
        }
      }
      // Path heuristics — redact PAN-like digit runs and email-like strings.
      u.pathname = u.pathname
        .replace(/\d{13,19}/g, "[PAN]")
        .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[EMAIL]");
      return u.toString();
    } catch {
      return "[INVALID_URL]";
    }
  };

  // -------------------------------------------------------------------------
  // Resource attributes — service identity in Kibana.
  // -------------------------------------------------------------------------

  const resource = resourceFromAttributes({
    "service.name": "endor-group/customer-web",
    "service.version": process.env.NEXT_PUBLIC_SENTRY_RELEASE ?? "unknown",
    "deployment.environment.name": env,
  });

  // -------------------------------------------------------------------------
  // Sampler — ParentBased(TraceIdRatio).
  //   Parent sampled  -> keep
  //   Parent dropped  -> drop
  //   No parent (root) -> apply ratio
  // Matches Sentry's 100% tracesSampleRate so the two systems sample in lockstep.
  // -------------------------------------------------------------------------

  const ratio = 1.0;
  const sampler = new ParentBasedSampler({
    root: new TraceIdRatioBasedSampler(ratio),
  });

  // -------------------------------------------------------------------------
  // OTLP/HTTP JSON exporter → the BFF's /diag/ingest, a cross-origin
  // keep-alive fetch (see the file header). The base ends in `/`
  // (next.config.mjs), so the path is resolved against it.
  // -------------------------------------------------------------------------

  const exporter = new OTLPTraceExporter({
    url: new URL("diag/ingest", bffBaseUrl()).href,
  });

  const provider = new WebTracerProvider({
    resource,
    sampler,
    // Wrap BatchSpanProcessor with the network-event stripper so timing
    // events are filtered before the OTLP exporter serialises them.
    spanProcessors: [
      new StripNetworkTimingEventsProcessor(
        new BatchSpanProcessor(exporter, {
          maxQueueSize: 100,
          maxExportBatchSize: 20,
          scheduledDelayMillis: 5_000,
          exportTimeoutMillis: 10_000,
        })
      ),
    ],
  });

  provider.register();

  // -------------------------------------------------------------------------
  // Instrumentations
  //
  // FetchInstrumentation only. We deliberately do NOT register
  // UserInteractionInstrumentation — clicks/submits that don't hit an API
  // shouldn't create traces. Business spans come from explicit withSpan()
  // calls in form handlers, and the API fetch they trigger becomes a child
  // via active context.
  // -------------------------------------------------------------------------

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Inject traceparent only on BFF calls: the BFF origin, an anchored
        // pattern (lib/bffTransport.ts). Without it the BFF span would start
        // a new trace.
        propagateTraceHeaderCorsUrls: bffPropagationTargets(),
        // Telemetry-of-telemetry, third-party SDKs and OIDC renewals — see
        // lib/otel/ignoreUrls.ts for the list and the reason for each.
        ignoreUrls: [...OTEL_FETCH_IGNORE_URLS],
        clearTimingResources: true,
        applyCustomAttributesOnSpan: (span, request, _response) => {
          const url =
            typeof request === "string"
              ? request
              : ((request as Request).url ?? "");
          if (url) {
            span.setAttribute("http.url", scrubUrl(url));
          }
        },
      }),
    ],
  });

  // -------------------------------------------------------------------------
  // Flush on pagehide — belt-and-braces alongside the batch processor's own
  // hide flush, through the same keep-alive exporter.
  // -------------------------------------------------------------------------

  window.addEventListener("pagehide", () => {
    // Best-effort unload flush — swallow rejections so a failed flush never
    // surfaces as an unhandled promise rejection (cf. the logs-SDK race behind
    // Sentry CUSTOMER-UI-3X). Telemetry must never break the user's session.
    provider.forceFlush().catch(() => {});
  });

  // Marker for tests/Sentry breadcrumbs to detect SDK presence.
  (window as unknown as { __upOtelReady?: boolean }).__upOtelReady = true;
}

export { trace };
